import * as React from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { useSkuTrend } from '@/hooks/useStock'

interface Props {
  skuId: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
  demo: boolean
  mask: (skuId: number, name: string) => string
}

const BRANCH_COLORS: Record<string, string> = {
  Putrajaya: '#2563eb',
  Bangi: '#10b981',
  'Shah Alam': '#f59e0b',
  'Kuala Lumpur': '#8b5cf6',
}

export function SkuSheet({ skuId, open, onOpenChange, demo, mask }: Props) {
  const { data, isLoading } = useSkuTrend(open ? skuId : null, 6)

  const { rows, branches } = React.useMemo(() => {
    if (!data?.series) return { rows: [] as any[], branches: [] as string[] }
    const byMonth = new Map<string, any>()
    const branchSet = new Set<string>()
    for (const s of data.series) {
      branchSet.add(s.branch)
      const r = byMonth.get(s.ym) ?? { ym: s.ym }
      r[s.branch] = s.qty
      byMonth.set(s.ym, r)
    }
    return {
      rows: [...byMonth.values()].sort((a, b) => a.ym.localeCompare(b.ym)),
      branches: [...branchSet],
    }
  }, [data])

  const title = data?.sku ? (skuId != null ? mask(skuId, data.sku) : data.sku) : 'SKU trend'

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            6-month demand per branch{data?.type ? ` · ${data.type}` : ''}. Divergent lines = rebalancing opportunity; a line dropping to zero while others continue = likely stockout.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 h-[320px]">
          {isLoading ? (
            <div className="h-full w-full animate-pulse rounded-lg bg-card" />
          ) : rows.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-text-muted">No history.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="ym" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {branches.map((b) => (
                  <Line
                    key={b}
                    type="monotone"
                    dataKey={b}
                    stroke={BRANCH_COLORS[b] ?? '#64748b'}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {!demo && (
          <p className="mt-4 text-xs text-text-muted">
            Tip: a branch that flatlines to zero mid-series while its sister branches keep selling is the
            stockout signature the alert system flags pre-emptively.
          </p>
        )}
      </SheetContent>
    </Sheet>
  )
}
